import { classifyType } from '@/lib/values';
import type { ColumnSchema } from '@/types/api';

export const ROW_HEIGHT = 26;
export const HEADER_HEIGHT = 40;
export const ROW_NUMBER_WIDTH = 58;

/** Width heuristic: enough for the header, sized to the type's usual content. */
export function columnWidth(column: ColumnSchema): number {
  const byType = (() => {
    switch (classifyType(column.type)) {
      case 'boolean':
        return 78;
      case 'number':
        return 112;
      case 'bigint':
        return 148;
      case 'temporal':
        return 172;
      case 'nested':
        return 210;
      case 'binary':
        return 150;
      default:
        return 190;
    }
  })();
  const byName = column.name.length * 7.4 + 56;
  return Math.min(420, Math.max(byType, byName));
}

export interface VisibleColumn {
  readonly column: ColumnSchema;
  /** Index into the original, unreordered result schema. */
  readonly sourceIndex: number;
  readonly width: number;
}

export const MIN_COLUMN_WIDTH = 56;
export const MAX_COLUMN_WIDTH = 900;

/** User-set widths, by column name. Overrides the type heuristic. */
export type ColumnWidths = Readonly<Record<string, number>>;

export function clampColumnWidth(width: number): number {
  if (!Number.isFinite(width)) return MIN_COLUMN_WIDTH;
  return Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)));
}

/**
 * Applies the user's column order and hidden set to the result schema.
 * `sourceIndex` is preserved so cell lookups stay correct after a reorder.
 */
export function visibleColumns(
  columns: readonly ColumnSchema[],
  order: readonly string[],
  hidden: readonly string[],
  widths: ColumnWidths = {},
): VisibleColumn[] {
  const widthFor = (column: ColumnSchema): number => {
    const override = widths[column.name];
    return typeof override === 'number' ? clampColumnWidth(override) : columnWidth(column);
  };

  const hiddenSet = new Set(hidden);
  const indexByName = new Map(columns.map((column, index) => [column.name, index]));

  const names = order.length > 0 ? order : columns.map((column) => column.name);
  const seen = new Set<string>();
  const ordered: VisibleColumn[] = [];

  for (const name of names) {
    if (seen.has(name) || hiddenSet.has(name)) continue;
    const index = indexByName.get(name);
    if (index === undefined) continue;
    const column = columns[index];
    if (!column) continue;
    seen.add(name);
    ordered.push({ column, sourceIndex: index, width: widthFor(column) });
  }

  // Columns added by a re-run that the saved order does not know about.
  columns.forEach((column, index) => {
    if (seen.has(column.name) || hiddenSet.has(column.name)) return;
    ordered.push({ column, sourceIndex: index, width: widthFor(column) });
  });

  return ordered;
}

export function moveColumn(
  order: readonly string[],
  from: string,
  to: string,
): string[] {
  const next = [...order];
  const fromIndex = next.indexOf(from);
  const toIndex = next.indexOf(to);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return next;
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, from);
  return next;
}
